import { useEffect, useMemo, useState } from "react";
import { CheckCircle, WarningCircle, MagnifyingGlass, FloppyDisk, Storefront, UploadSimple, MagicWand, Lightning, DownloadSimple, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export default function ShopifyMapping() {
  const [status, setStatus] = useState(null);
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [showBulk, setShowBulk] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [autoBusy, setAutoBusy] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);

  const syncInventory = async () => {
    setSyncBusy(true);
    try {
      const { data } = await api.post("/admin/shopify/sync-inventory");
      toast.success(`Synced ${data.updated} mapped products · ${data.sold_out} marked sold out`);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSyncBusy(false); }
  };

  const runBulk = async () => {
    const rows = bulkText.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).map((l) => {
      const idx = l.lastIndexOf(",");
      if (idx === -1) return null;
      return { match: l.slice(0, idx).trim().replace(/^"|"$/g, ""), variant_id: l.slice(idx + 1).trim().replace(/^"|"$/g, "") };
    }).filter((r) => r && r.variant_id && r.match.toLowerCase() !== "name");
    if (rows.length === 0) return toast.error("Paste rows like: Product Name, variant_id");
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/products/variant-bulk", { rows });
      toast.success(`Mapped ${data.matched} · ${data.unmatched.length} unmatched`);
      if (data.unmatched.length) toast.message("Unmatched: " + data.unmatched.slice(0, 5).join(", ") + (data.unmatched.length > 5 ? "…" : ""));
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBulkBusy(false); }
  };

  const autoMatch = async () => {
    setAutoBusy(true);
    try {
      const { data } = await api.post("/admin/shopify/auto-match");
      toast.success(`Auto-matched ${data.matched} products (from ${data.total_shopify} in Shopify)`);
      if (data.unmatched?.length) toast.message(`${data.unmatched.length} not matched by name — map those manually`);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setAutoBusy(false); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/admin/products/variant-template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "wigglez-variant-map.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Downloaded — fill the variant_id column, then paste back into Bulk import");
    } catch (e) { toast.error("Download failed"); }
  };

  const load = () => {
    api.get("/shopify/status").then(({ data }) => setStatus(data)).catch(() => {});
    api.get("/products").then(({ data }) => {
      setProducts(data);
      const d = {};
      data.forEach((p) => { d[p.id] = p.shopify_variant_id || ""; });
      setDrafts(d);
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async (p) => {
    setSavingId(p.id);
    try {
      const val = (drafts[p.id] || "").trim();
      await api.put(`/admin/products/${p.id}/variant`, { shopify_variant_id: val });
      setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, shopify_variant_id: val } : x)));
      toast.success(`Mapped ${p.name}`);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSavingId(null); }
  };

  const filtered = useMemo(
    () => products.filter((p) => p.name.toLowerCase().includes(q.toLowerCase())),
    [products, q]
  );
  const mapped = products.filter((p) => p.shopify_variant_id).length;
  const connected = status?.connected;

  return (
    <div data-testid="admin-shopify">
      <div className="flex items-center gap-3 mb-2">
        <Storefront weight="fill" size={20} className="text-secondary" />
        <h2 className="font-display text-2xl">Shopify · Checkout Engine</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-6 max-w-2xl">
        Emergent is your storefront; Shopify is the register. Map each product to its Shopify <b>variant ID</b> and — once connected — checkout flows straight into your Shopify dashboard.
      </p>

      {/* Connection status */}
      <div className={`rounded-2xl border p-5 mb-6 ${connected ? "border-secondary/40 bg-secondary/5" : "border-amber-400/40 bg-amber-400/5"}`} data-testid="shopify-status-card">
        <div className="flex items-center gap-2 mb-1">
          {connected ? <CheckCircle weight="fill" size={20} className="text-secondary" /> : <WarningCircle weight="fill" size={20} className="text-amber-400" />}
          <p className="font-modern font-medium">{connected ? "Shopify is connected — checkout is live" : "Shopify is not connected yet"}</p>
        </div>
        {!connected && (
          <p className="text-sm text-muted-foreground">
            Add <code className="text-secondary">SHOPIFY_STORE_DOMAIN</code> (e.g. your-store.myshopify.com) and <code className="text-secondary">SHOPIFY_STOREFRONT_TOKEN</code> in the <b>Secrets</b> tab, then reload. Storefront token: Shopify Admin → Settings → Apps and sales channels → Develop apps → your app → Storefront API access token.
          </p>
        )}
        <p className="text-xs text-muted-foreground mt-2">{mapped} / {products.length} products mapped to a Shopify variant</p>
      </div>

      {/* Bulk tools */}
      <div className="rounded-2xl border border-white/10 bg-card p-5 mb-6" data-testid="shopify-bulk-tools">
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={autoMatch} disabled={autoBusy || !status?.admin_set}
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-full text-sm font-modern disabled:opacity-40" data-testid="shopify-automatch">
            <MagicWand weight="fill" size={15} /> {autoBusy ? "Matching…" : "Auto-match from Shopify"}
          </button>
          <button onClick={() => setShowBulk((v) => !v)} className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 rounded-full text-sm font-modern hover:border-secondary transition-colors" data-testid="shopify-bulk-toggle">
            <UploadSimple weight="bold" size={15} /> Bulk import (CSV paste)
          </button>
          <button onClick={downloadTemplate} className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 rounded-full text-sm font-modern hover:border-secondary transition-colors" data-testid="shopify-template-download">
            <DownloadSimple weight="bold" size={15} /> Download name→variant CSV
          </button>
          <button onClick={syncInventory} disabled={syncBusy || !status?.admin_set} className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 rounded-full text-sm font-modern hover:border-secondary transition-colors disabled:opacity-40" data-testid="shopify-sync-inventory">
            <ArrowsClockwise weight="bold" size={15} /> {syncBusy ? "Syncing…" : "Sync inventory"}
          </button>
          {!status?.admin_set && <span className="text-xs text-muted-foreground">Auto-match needs SHOPIFY_ADMIN_TOKEN in Secrets</span>}
        </div>
        {showBulk && (
          <div className="mt-4" data-testid="shopify-bulk-panel">
            <p className="text-xs text-muted-foreground mb-2">One product per line: <code className="text-secondary">Product Name, variant_id</code> (a header row "name,variant_id" is skipped). Variant IDs can be numeric or gid://…</p>
            <textarea value={bulkText} onChange={(e) => setBulkText(e.target.value)} rows={6}
              placeholder={"Wigglez Oversized Hoodie — Hot Pink, 44991234567890\nScarlett 69 Hoodie, gid://shopify/ProductVariant/44991299999"}
              className="w-full bg-transparent border border-white/15 rounded-[10px] px-4 py-3 text-sm outline-none focus:border-secondary resize-none font-mono" data-testid="shopify-bulk-text" />
            <button onClick={runBulk} disabled={bulkBusy} className="mt-3 inline-flex items-center gap-2 bg-secondary text-black px-5 py-2.5 rounded-full text-sm font-modern disabled:opacity-50" data-testid="shopify-bulk-run">
              <Lightning weight="fill" size={15} /> {bulkBusy ? "Importing…" : "Map from paste"}
            </button>
          </div>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-sm mb-5">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…"
          className="w-full bg-transparent border border-white/15 rounded-full pl-9 pr-4 py-2.5 text-sm outline-none focus:border-secondary" data-testid="shopify-search" />
      </div>

      {/* Mapping list */}
      <div className="space-y-2">
        {filtered.map((p) => (
          <div key={p.id} className="flex items-center gap-3 p-3 rounded-lg bg-card border border-white/5" data-testid={`shopify-map-${p.id}`}>
            <div className="h-11 w-11 rounded-lg overflow-hidden bg-muted shrink-0">{p.images?.[0] && <img src={p.images[0]} alt="" className="h-full w-full object-cover" />}</div>
            <div className="flex-1 min-w-0">
              <p className="font-modern text-sm truncate">{p.name}</p>
              <p className="text-xs text-muted-foreground">{p.category} · ${p.price}{p.shopify_variant_id ? " · mapped" : ""}</p>
            </div>
            <input value={drafts[p.id] ?? ""} onChange={(e) => setDrafts((d) => ({ ...d, [p.id]: e.target.value }))}
              placeholder="Shopify variant ID (or gid://…)"
              className="w-56 bg-transparent border border-white/15 rounded-lg px-3 py-2 text-xs outline-none focus:border-secondary" data-testid={`shopify-variant-input-${p.id}`} />
            <button onClick={() => save(p)} disabled={savingId === p.id}
              className="inline-flex items-center gap-1.5 bg-secondary text-black px-3 py-2 rounded-lg text-xs font-modern disabled:opacity-50" data-testid={`shopify-variant-save-${p.id}`}>
              <FloppyDisk weight="fill" size={14} /> {savingId === p.id ? "…" : "Save"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
